import { createSelector } from "@reduxjs/toolkit";
import { adaptSelectors } from "./adaptSlice";
import { StoreState } from "../store";
import { CustomizationObjectType, CustomizationOutput, ValidationStatus } from "../global-types/customization-types";
import { BackendValidationErrorViewModel, FormValidationError } from "../global-types/store-errors";
import { generateCustomizationTreeViewItems, SelectorWithInputArg } from "../global-types/customizationSelectors";
import { isAdaptCustomizationOutput } from "./adapt-types";
import { CommonAdaptSelectors } from "../global-types/modelTypeSelectorHelper/commonAdaptSelectors";


const commonSelectors = new CommonAdaptSelectors();

export const adaptCommonSelectors = {
    selectDeprecatedModels: (state: StoreState) => commonSelectors.selectDeprecatedModels(state.adapt),
    selectNonLicensedModels: (state: StoreState) => commonSelectors.selectNonLicensedModels(state.adapt),
    selectUserHiddenModels: (state: StoreState) => commonSelectors.selectUserHiddenModels(state.adapt),
    selectLatestModels: (state: StoreState) => commonSelectors.selectLatestModels(state.adapt),
    selectModelsInAlphabeticalOrder: (state: StoreState) => commonSelectors.selectModelsInAlphabeticalOrder(state.adapt),
    selectAllVersionTagsInOrder: (state: StoreState) => commonSelectors.selectAllVersionTagsInOrder(state.adapt),
    selectAllTagsInOrder: (state: StoreState) => commonSelectors.selectAllTagsInOrder(state.adapt),
    selectInputModalities: (state: StoreState) => commonSelectors.selectInputModalities(state.adapt),
    selectOutputModalities: (state: StoreState) => commonSelectors.selectOutputModalities(state.adapt),
}

/** Generate a tree view hierarchy of models and some of their child objects. This is used by the customization page side navigation.
 * This data isn't ready-to-go for Patternfly's TreeView component but the separate convertTreeViewDataIntoItemHierarchy helper function
 * in customization-helpers.tsx is used to generate the appropriate data from what this function produces.
 */
export const selectAdaptTreeViewItems = createSelector([
    (state: StoreState) => adaptCommonSelectors.selectModelsInAlphabeticalOrder(state),
    (state: StoreState) => adaptSelectors.selectCustomizationBasesInAlphabeticalOrder(state),
    (state: StoreState) => adaptSelectors.selectOutputEntities(state),
    (state: StoreState) => adaptSelectors.selectOutputMetadataEntities(state),
    (state: StoreState) => adaptSelectors.selectCustomizationValidationErrorEntities(state),
    (state: StoreState) => adaptSelectors.selectFormValidationErrors(state),
    (state: StoreState) => adaptSelectors.selectDicomRuleEntities(state),
],
    (models, customizationBases, outputEntities, metadataEntities, validationEntities, formValidationErrors, dicomRuleEntities) => {

        // create an inline validation function that will be supplied to tree view generator function
        const areOutputChildrenValid = (output: CustomizationOutput, validationEntities: Record<string, BackendValidationErrorViewModel>, _formErrors: FormValidationError[]): ValidationStatus => {
            if (!isAdaptCustomizationOutput(output)) {
                throw new Error('Invalid output type');
            }

            const hasBackendError = !output.roiSelection.roiRules.every(rId => !validationEntities[rId] || validationEntities[rId]!.type !== CustomizationObjectType.AdaptRoiRule);
            return hasBackendError ? ValidationStatus.Error : ValidationStatus.Valid;
        }

        const results = generateCustomizationTreeViewItems(models, customizationBases, outputEntities, metadataEntities, validationEntities, formValidationErrors, areOutputChildrenValid, dicomRuleEntities);
        return results;
    });

/**
* Factory function for making a memoized selector that returns true if any metadata for given output id is modified.
*/
export const makeSelectIsAnyMetadataForOutputModified = (): SelectorWithInputArg<boolean, string> => createSelector(
    [
        (state: StoreState, customizationOutputId: string) => adaptSelectors.selectOutputById(state, customizationOutputId),
        (state: StoreState) => adaptSelectors.selectOutputMetadataEntities(state)
    ],
    (customizationOutput, metadataEntities) => customizationOutput?.metadata.some(mId => metadataEntities[mId]?.isModified) || false
);

/**
 * Factory function for making a memoized selector that returns true if any selection rule (trigger) for a customization base id is modified.
 */
export const makeSelectIsAnyTriggerForBaseModified = (): SelectorWithInputArg<boolean, string> => createSelector(
    [
        (state: StoreState, customizationBaseId) => adaptSelectors.selectCustomizationBaseById(state, customizationBaseId),
        (state: StoreState) => adaptSelectors.selectAeTitleRuleEntities(state),
        (state: StoreState) => adaptSelectors.selectDicomRuleEntities(state)
    ],
    (customizationBase, aeTitleRuleEntities, dicomRuleEntities) => {
        const isAnyAeTitleRuleModified = customizationBase?.aeTitleRules.some(aId => aeTitleRuleEntities[aId]?.isModified) || false;
        if (isAnyAeTitleRuleModified) { return true; }
        return customizationBase?.dicomRules.some(dId => dicomRuleEntities[dId]?.isModified === true) || false;
    }
);

/** Factory function for making a memoized selector that returns all form validation errors objects that are related to a specific dicom rule. */
export const makeSelectFormValidationErrorsForDicomRule = (): SelectorWithInputArg<FormValidationError[], string> => createSelector(
    [(_, dicomRuleId: string) => dicomRuleId, adaptSelectors.selectFormValidationErrors],
    (dicomRuleId, formValidationErrors) => formValidationErrors.filter(e => e.itemId === dicomRuleId && e.itemType === CustomizationObjectType.DicomRule)
);

/** Factory function for making a memoized selector that returns all form validation errors objects that are related to a specific dicom attribute rule. */
export const makeSelectFormValidationErrorsForDicomAttributeRule = (): SelectorWithInputArg<FormValidationError[], string> => createSelector(
    [(_, dicomAttributeRuleId: string) => dicomAttributeRuleId, adaptSelectors.selectFormValidationErrors],
    (dicomAttributeRuleId, formValidationErrors) => formValidationErrors.filter(e => e.itemId === dicomAttributeRuleId && e.itemType === CustomizationObjectType.DicomAttributeRule)
);

/** Factory function for making a memoized selector that returns all form validation errors objects that are related to a specific AE Title rule. */
export const makeSelectFormValidationErrorsForAeTitleRule = (): SelectorWithInputArg<FormValidationError[], string> => createSelector(
    [(_, aeTitleRuleId: string) => aeTitleRuleId, adaptSelectors.selectFormValidationErrors],
    (aeTitleRuleId, formValidationErrors) => formValidationErrors.filter(e => e.itemId === aeTitleRuleId && e.itemType === CustomizationObjectType.AeTitleRule)
);
